'use client';

import { useParams, useNavigate } from 'react-router-dom';
import { Mail, Phone, PenLine } from 'lucide-react';
import { Button } from '../components/ui/button.tsx';
import { Card } from '../components/ui/card.tsx';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs.tsx';
import { WeeklySchedule } from '../components/employee/WeeklySchedule.tsx';
import { PerformanceCard } from '../components/employee/PerformanceCard.jsx';
import { DocumentsCard } from '../components/employee/DocumentsCard.jsx';
import { ColleaguesCard } from '../components/employee/ColleagueCard.jsx';
import { ClassPerformance } from '../components/employee/ClassPerformance';
import { useGetStaffById } from '../api/staff/staffQueries';
import { LoadingSpinner } from '../components/ui/loading-spinner.tsx';
import { StaffEditPanel } from '../components/employee/StaffEditPanel';
import { useState } from 'react';
import Schedule from '../components/events/Schedule.jsx';
import ScheduleOverview from '../components/events/ScheduleOverview.jsx';

export function EmployeeProfile() {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const { data: staff, isLoading, error } = useGetStaffById(staffId);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mb-4" />
          <p className="text-muted-foreground">Loading staff profile...</p>
        </div>
      </div>
    );
  }

  if (error || !staff) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-100 text-red-800 p-4 rounded-lg mb-4">
            <p className="font-semibold mb-2">Error loading staff data</p>
            <p>{error?.message || 'Staff member not found'}</p>
          </div>
          <Button onClick={() => navigate('/team')} variant="secondary">
            Back to Team
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="flex-1 overflow-y-auto">
        <div className="p-6">
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                className="gap-2"
                onClick={() => navigate('/team')}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 15 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.85355 3.85355C7.04882 3.65829 7.04882 3.34171 6.85355 3.14645C6.65829 2.95118 6.34171 2.95118 6.14645 3.14645L2.14645 7.14645C1.95118 7.34171 1.95118 7.65829 2.14645 7.85355L6.14645 11.8536C6.34171 12.0488 6.65829 12.0488 6.85355 11.8536C7.04882 11.6583 7.04882 11.3417 6.85355 11.1464L3.20711 7.5L6.85355 3.85355Z"
                    fill="currentColor"
                    fillRule="evenodd"
                    clipRule="evenodd"
                  ></path>
                </svg>
                Back
              </Button>
            </div>
            <Button onClick={() => setIsEditPanelOpen(true)}>
              <PenLine className="mr-2 h-4 w-4" /> Edit Profile
            </Button>
          </div>

          <Card className="mb-6">
            <div className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-4">
                  <div className="relative h-20 w-20 rounded-full bg-gray-100">
                    <img
                      src={staff.photoUrl || '/placeholder.svg'}
                      alt={staff.name}
                      className="h-full w-full rounded-full object-cover"
                    />
                    <span className="absolute bottom-0 right-0 h-4 w-4 rounded-full border-2 border-white bg-green-400"></span>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">{staff.name}</h2>
                    <p className="text-sm text-gray-500">
                      {staff.role || staff.designation}
                    </p>
                    <div className="mt-2 flex items-center gap-4">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Mail className="h-4 w-4" />
                        <span>{staff.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Phone className="h-4 w-4" />
                        <span>{staff.phoneNumber || 'Not provided'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0">
              <TabsTrigger
                value="overview"
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              >
                Overview
              </TabsTrigger>
              <TabsTrigger
                value="schedule"
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              >
                Schedule
              </TabsTrigger>
              {/* <TabsTrigger
                value="performance"
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              >
                Performance
              </TabsTrigger> */}
            </TabsList>
            <TabsContent value="overview">
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-7">
                <div className="space-y-6 lg:col-span-4">
                  <ScheduleOverview staffId={staffId} />
                  {/* <WeeklySchedule /> */}
                  <PerformanceCard />
                </div>
                <div className="space-y-6 lg:col-span-3">
                  <DocumentsCard />
                  <ColleaguesCard />
                </div>
              </div>
            </TabsContent>
            <TabsContent value="schedule">
              <Schedule staffId={staffId} />
              {/* <WeeklySchedule /> */}
            </TabsContent>
            {/* <TabsContent value="performance" className="space-y-6">
              <ClassPerformance />
            </TabsContent> */}
          </Tabs>
        </div>

        <StaffEditPanel
          staff={staff}
          isOpen={isEditPanelOpen}
          onClose={() => setIsEditPanelOpen(false)}
        />
      </main>
    </div>
  );
}
